import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Layout from '../layout/Layout';
import { submitContactForm } from '../lib/api';

export default function Contact() {
  const location = useLocation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (location.state?.subject) {
      setForm(prev => ({ ...prev, subject: location.state.subject }));
    }
  }, [location.state]);

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      await submitContactForm(form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  }

  const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-lg text-brand-dark placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors bg-white text-sm";

  return (
    <Layout>
      <style>{`
  .branch-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    max-width: 480px;
    margin: 0 auto;
  }
  @media (max-width: 480px) {
    .branch-grid {
      grid-template-columns: 1fr;
      max-width: 260px;
      gap: 28px;
    }
  }
  .branch-item {
    text-align: center;
    padding: 24px 0;
    transition: transform 0.3s ease;
  }
  .branch-item:hover { transform: translateY(-4px); }
  .branch-flag {
    width: 96px;
    height: 96px;
    border-radius: 9999px;
    overflow: hidden;
    margin: 0 auto 16px;
    border: 4px solid rgba(0,165,181,0.2);
    box-shadow: 0 4px 12px rgba(16,34,60,0.08);
    transition: transform 0.35s ease, box-shadow 0.3s ease;
  }
  @media (max-width: 480px) {
    .branch-flag { width: 80px; height: 80px; }
  }
  .branch-item:hover .branch-flag {
    transform: scale(1.06);
    box-shadow: 0 10px 24px rgba(0,165,181,0.25);
  }
  .branch-name {
    font-weight: 700;
    color: #0B1F3F;
  }
`}</style>
      {/* Page Header */}
      <section className="relative overflow-hidden min-h-[280px] md:min-h-[360px] flex items-center bg-brand-dark">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40" style={{ backgroundImage: 'url(/assets/images/banner/contact.jpg)' }}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/30 to-brand/5"></div>
        <div className="container relative z-10 py-20">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold font-heading text-white mb-4">Contact</h2>
            <ul className="flex justify-center items-center gap-3 text-white/70 text-sm list-none p-0 m-0">
              <li><Link className="hover:text-brand transition-colors" to="/">Home</Link></li>
              <li><span className="text-white/40">/</span></li>
              <li className="text-brand">Contact</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-brand-dark mb-3">
              We welcome any inquiries or messages you may have.
            </h2>
            <p className="text-gray-500 text-sm">You can also reach out to us by phone or email</p>
          </div>
          <div className="row gutter-y-30">
            {[
              { iconClass: 'icon-location-filled', bg: 'bg-brand', label: 'Address', lines: ['44 Abelard Ave, Brampton, ON L6Y2K8, Canada.'] },
              { iconClass: 'icon-phone-auricular', bg: 'bg-brand', label: 'Get In Touch', lines: ['+1(647)-687-4226'], hrefs: ['tel:+16476874226'] },
              { iconClass: 'icon-email2', bg: 'bg-brand', label: 'Quick Email us', lines: ['magizhinitech@gmail.com'], hrefs: ['mailto:magizhinitech@gmail.com'] },
            ].map(({ iconClass, bg, label, lines, hrefs }, i) => (
              <div className="col-xl-4 col-lg-4" key={i}>
                <div className="flex items-start gap-4 bg-white rounded-2xl shadow-md p-6">
                  <div className={`flex-shrink-0 w-14 h-14 rounded-full ${bg} flex items-center justify-center text-white text-xl`}>
                    <span className={iconClass}></span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-widest font-medium mb-1">{label}</p>
                    {lines.map((line, j) => (
                      hrefs ? (
                        <h3 key={j} className="text-brand-dark font-semibold text-sm leading-relaxed">
                          <a href={hrefs[j]} className="hover:text-brand transition-colors">{line}</a>
                        </h3>
                      ) : (
                        <h3 key={j} className="text-brand-dark font-semibold text-sm leading-relaxed">{line}</h3>
                      )
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <div className="absolute inset-0 bg-cover bg-center opacity-5" style={{ backgroundImage: 'url(/assets/images/pattern/contact-page-pattern.jpg)' }}></div>
              <div className="relative z-10 bg-brand-dark/95 p-8 md:p-12 rounded-2xl">
                <h2 className="text-2xl font-bold font-heading text-white mb-8 text-center">Send Us a Message</h2>

                {status === 'success' && (
                  <div className="mb-6 px-4 py-3 bg-green-500/20 border border-green-500/30 text-green-300 rounded-lg text-sm">
                    Your message has been sent successfully!
                  </div>
                )}
                {status === 'error' && (
                  <div className="mb-6 px-4 py-3 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg text-sm">
                    {errorMsg}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="row">
                    <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
                      <div className="mb-4">
                        <input type="text" placeholder="Full Name" name="name" value={form.name} onChange={handleChange} required className={inputClass} />
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
                      <div className="mb-4">
                        <input type="email" placeholder="Email Address" name="email" value={form.email} onChange={handleChange} required className={inputClass} />
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
                      <div className="mb-4">
                        <input type="text" placeholder="Phone" name="phone" value={form.phone} onChange={handleChange} required className={inputClass} />
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
                      <div className="mb-4">
                        <input type="text" placeholder="Subject" name="subject" value={form.subject} onChange={handleChange} required className={inputClass} />
                      </div>
                    </div>
                    <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12">
                      <div className="mb-6">
                        <textarea
                          name="message"
                          placeholder="Your Message here"
                          value={form.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          className={inputClass + ' resize-none'}
                        ></textarea>
                      </div>
                      <div className="text-center">
                        <button
                          type="submit"
                          disabled={status === 'sending'}
                          className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-8 py-3 rounded-full hover:bg-white hover:text-brand-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {status === 'sending' ? 'Sending…' : 'Send A Message'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map */}
      <section className="w-full">
        <iframe
          src="https://maps.google.com/maps?q=44+Abelard+Ave,+Brampton,+ON+L6Y2K8,+Canada&t=&z=15&ie=UTF8&iwloc=&output=embed"
          className="w-full h-72 md:h-96 border-0 block"
          allowFullScreen=""
          title="Office map"
        ></iframe>
      </section>

    {/* Upcoming Branches */}
<section className="py-16 bg-gray-50">
  <div className="container">
    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
      <h2 className="text-2xl font-bold font-heading text-brand-dark text-center mb-10">Upcoming Branches at</h2>
      <div className="branch-grid">
        {[
          { flag: '/assets/images/flags/Usa.png', name: 'USA' },
          { flag: '/assets/images/flags/india.png', name: 'India' },
        ].map(({ flag, name }) => (
          <div className="branch-item" key={name}>
            <div className="branch-flag">
              <img src={flag} alt={name} className="w-full h-full object-cover" />
            </div>
            <h2 className="branch-name text-lg font-heading">{name}</h2>
          </div>
        ))}
      </div>
    </div>
  </div>
</section>
    </Layout>
  );
}